import React, { useEffect } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useUser } from '../contexts/UserContext';
import { CircularProgress, Box, Typography } from '@mui/material';

interface SubscriptionGuardProps {
   children: React.ReactNode;
   redirectOnNoSubscription?: boolean; // If true, redirects instead of showing modal
}

/**
 * SubscriptionGuard - Protects components/pages based on subscription status
 * 
 * Logic:
 * 1. Invited users: Always get access (bypass subscription requirement)
 * 2. Non-invited users with no subscription history: Redirect to public subscription page
 * 3. Non-invited users with subscription history (even expired): Show subscription modal
 * 4. Users with active subscription: Full access
 */
export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ 
   children, 
   redirectOnNoSubscription = false 
}) => {
   const { currentUser } = useUser();
   const { 
      loading, 
      invitationLoading, 
      isInvitedUser, 
      currentSubscription, 
      hasSubscriptions,
      checkSubscriptionStatus 
   } = useSubscription();

   useEffect(() => {
      // Only run protection check when data is loaded
      if (!loading && !invitationLoading && currentUser) {
         const hasCurrentSubscription = currentSubscription !== null;
         
         console.log('🛡️ SubscriptionGuard: Running protection check', {
            isInvitedUser,
            hasCurrentSubscription,
            hasActiveSubscription: hasSubscriptions,
            redirectOnNoSubscription,
            userEmail: currentUser?.email,
            currentPath: window.location.pathname,
         });

         // 🚨 IMMEDIATE REDIRECT: Non-invited users with no subscription history
         if (!isInvitedUser && !hasCurrentSubscription && redirectOnNoSubscription) {
            console.log('🚨 SubscriptionGuard: IMMEDIATE REDIRECT - Non-invited user with no subscription history');
            if (currentUser?.uid && currentUser?.email) {
               const redirectUrl = `/public-subscription?userId=${currentUser.uid}&email=${encodeURIComponent(currentUser.email)}&reason=guard_protection&returnUrl=${encodeURIComponent(window.location.pathname)}`;
               console.log('🚨 Redirecting to:', redirectUrl);
               window.location.replace(redirectUrl); // Use replace to prevent back button issues
               return; // Stop further execution
            }
         }
      }
   }, [loading, invitationLoading, currentUser, isInvitedUser, currentSubscription, hasSubscriptions, redirectOnNoSubscription]);

   // Show loading while data is being fetched
   if (loading || invitationLoading) {
      return (
         <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="200px"
            gap={2}
         >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
               Checking subscription status...
            </Typography>
         </Box>
      );
   }

   // For invited users, always show content (they bypass subscription requirements)
   if (isInvitedUser) {
      console.log('✅ SubscriptionGuard: Invited user - granting access');
      return <>{children}</>;
   }

   // For non-invited users with active subscription, show content
   if (hasSubscriptions) {
      console.log('✅ SubscriptionGuard: User with active subscription - granting access');
      return <>{children}</>;
   }

   // For non-invited users without subscription history, show redirecting message
   if (!isInvitedUser && !currentSubscription && redirectOnNoSubscription) {
      console.log('🚨 SubscriptionGuard: Non-invited user with no subscription - showing redirect message');
      return (
         <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="400px"
            gap={2}
         >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.primary">
               Subscription Required
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
               Redirecting to subscription page...
            </Typography>
         </Box>
      );
   }

   // For all other cases (users with subscription history but no active subscription),
   // show content and let the subscription modal handle the UI
   console.log('⚠️ SubscriptionGuard: User with subscription history but no active access - showing content with modal');
   return <>{children}</>;
};

export default SubscriptionGuard;
